import { EmptySpaceFiller, GroupLabel } from './SharedGroupRuleElements';
import {
  GetInvalidGroupingSelectOptions,
  PerspectiveGroupingRule,
  PerspectivesGroupsTags,
  PerspectivesGroupsWorkspace,
  TagGroupingRule,
} from '../types';
import { Select } from '@ardoq/select';
import { ArdoqId, GroupType } from '@ardoq/api-types';
import { getWorkspaceOptions } from './getWorkspaceOptions';
import { DisabledOptionProps } from '../../types';
import { isTruthy } from '@ardoq/common-helpers';
import { type I18nLocale, t } from '@ardoq/i18n';

type TagGroupRuleProps = {
  groupingRule: TagGroupingRule;
  workspaces: PerspectivesGroupsWorkspace[];
  tagsPerWorkspace: Record<ArdoqId, PerspectivesGroupsTags[]>;
  onGroupingRuleUpdated: (
    groupingRuleId: string,
    updatedGroupingRule: Partial<PerspectiveGroupingRule>
  ) => void;
  tabIndex?: number;
  disabledOptionProps?: DisabledOptionProps;
  getInvalidGroupingSelectOptions?: GetInvalidGroupingSelectOptions;
  i18nLocale: I18nLocale;
};

const toTagOption = (tag: PerspectivesGroupsTags) => ({
  value: tag.id,
  label: tag.name,
});

const TagGroupRule = ({
  workspaces,
  groupingRule,
  tagsPerWorkspace,
  onGroupingRuleUpdated,
  disabledOptionProps,
  getInvalidGroupingSelectOptions,
  tabIndex,
  i18nLocale,
}: TagGroupRuleProps) => {
  const { workspaceSelectOption, ruleTargetSelectOption } =
    getInvalidGroupingSelectOptions?.(groupingRule) || {};

  const workspaceOptions = getWorkspaceOptions({
    workspaces,
    disabledOptionProps,
    entitiesName: 'tags',
    entitiesPerWorkspace: tagsPerWorkspace,
    firstSelectOption: workspaceSelectOption,
  });

  const tags = tagsPerWorkspace[groupingRule.workspaceId!] ?? [];
  const tagOptions = [ruleTargetSelectOption, ...tags.map(toTagOption)].filter(
    isTruthy
  );

  const updateGroupRule = (
    partialGroupingRule: Partial<PerspectiveGroupingRule>
  ) => onGroupingRuleUpdated(groupingRule.id, partialGroupingRule);

  return (
    <>
      <GroupLabel data-testid={`group-by-${GroupType.TAG}-label`}>
        {t(i18nLocale, 'perspectives:groupingRules.tagLabel', 'Tag')}
      </GroupLabel>

      <Select
        value={groupingRule.workspaceId}
        options={workspaceOptions}
        aria-label={t(
          i18nLocale,
          'perspectives:groupingRules.workspaceAriaLabel',
          'Workspace'
        )}
        dataTestId="tag-grouping-rule-workspace-select"
        tabIndex={tabIndex}
        onChange={event => {
          const nextWorkspaceId = event?.value;
          if (!nextWorkspaceId) return;
          const defaultTagId = tagsPerWorkspace[nextWorkspaceId]?.[0]?.id;
          updateGroupRule({
            workspaceId: nextWorkspaceId,
            targetId: defaultTagId,
          });
        }}
      />

      <Select
        aria-label={t(
          i18nLocale,
          'perspectives:groupingRules.tagsAriaLabel',
          'Tags'
        )}
        options={tagOptions}
        dataTestId="tag-grouping-rule-tag-select"
        value={groupingRule.targetId}
        tabIndex={tabIndex}
        onChange={event =>
          updateGroupRule({
            targetId: event?.value,
          })
        }
      />

      <EmptySpaceFiller />
      <EmptySpaceFiller />
    </>
  );
};

export { TagGroupRule };
